import React, { useState, useEffect, useMemo } from "react"
import { Row, Col, Card, Form, InputGroup, Button } from "react-bootstrap"
import { useNavigate, Link } from "react-router-dom"
import useAuthAxios from "../hooks/useAuthAxios"
import Layout from "../components/layout/Layout"
import DataTable from "../components/common/DataTable"
import { useAuth } from "../context/AuthContext"
import ExpertStats from "../components/dashboard/ExpertStats"

const Dashboard = () => {
  const [stats, setStats] = useState(null)
  const [expertStats, setExpertStats] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [statsLoading, setStatsLoading] = useState(true)
  const [statsError, setStatsError] = useState(null)
  const [expertStatsLoading, setExpertStatsLoading] = useState(true)
  const [expertStatsError, setExpertStatsError] = useState(null)
  const [rawStats, setRawStats] = useState(null)
  const { get } = useAuthAxios()
  const navigate = useNavigate()
  const { isAdmin, getAppLookupValue, appLookups } = useAuth()

  const statusLookups = useMemo(
    () => appLookups?.filter(lookup => lookup.Table === "Status") || [],
    [appLookups]
  )

  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true)
      setStatsError(null)
      try {
        const response = await get("/stats")
        setRawStats(response.data)
      } catch (error) {
        console.error("Αποτυχία λήψης στατιστικών:", error)
        setStatsError("Αποτυχία φόρτωσης στατιστικών")
      } finally {
        setStatsLoading(false)
      }
    }

    const fetchExpertStats = async () => {
      setExpertStatsLoading(true)
      setExpertStatsError(null)
      try {
        const response = await get("/stats", { params: { ExpertID: 0 } })
        setExpertStats(response.data)
      } catch (error) {
        console.error("Αποτυχία λήψης στατιστικών ανά πραγματογνώμονα:", error)
        setExpertStatsError("Αποτυχία φόρτωσης στατιστικών ανά πραγματογνώμονα")
      } finally {
        setExpertStatsLoading(false)
      }
    }

    if (isAdmin()) {
      fetchStats()
      fetchExpertStats()
    } else {
      fetchStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get, isAdmin])

  useEffect(() => {
    if (!appLookups || !rawStats) return

    const calculatedStats = statusLookups.reduce((acc, status) => {
      acc[`status${status.Code}Inspections`] = rawStats
        ?.filter(stat => stat.StatusCode === status.Code)
        ?.reduce((sum, stat) => sum + stat.Counter, 0)
      return acc
    }, {})

    setStats(calculatedStats)
  }, [appLookups, rawStats, statusLookups])

  const handleSearch = async e => {
    e.preventDefault()
    if (!searchQuery.trim()) return

    setSearching(true)
    setSearchError(null)
    setSearchResults([])
    setHasSearched(true)

    try {
      const response = await get(`/quickSearch?criterio=${encodeURIComponent(searchQuery.trim())}`)

      if (response.success && response.data?.QuickSearch) {
        setSearchResults(response.data.QuickSearch)
      } else {
        setSearchResults([])
      }
    } catch (error) {
      console.error("Αποτυχία γρήγορης αναζήτησης:", error)
      if (error.response?.status !== 404) {
        setSearchError("Αποτυχία αναζήτησης. Παρακαλώ προσπαθήστε ξανά.")
      }
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }

  const handleInspectionClick = inspectionId => {
    navigate(`/inspections/${inspectionId}`)
  }

  // Add custom styles for disabled fields
  useEffect(() => {
    const style = document.createElement("style")
    style.textContent = `
      .form-control:disabled {
        background-color:rgba(255, 255, 255, 0.3) !important;
      }
    `
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  return (
    <Layout>
      <div className="mb-4">
        {/* Statistics */}
        <div className="content-glass">
          <div className="dashboard-stats-container">
            {statusLookups
              .filter(status => isAdmin() || status.Code !== 0)
              .map(status => (
                <Link
                  key={status.Code}
                  to="/inspections"
                  className="stats-link"
                  state={{ status: status.Code }}>
                  <div className={`stats-box status-${status.Code}`}>
                    {statsLoading ? (
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-light" role="status">
                          <span className="visually-hidden">Φόρτωση...</span>
                        </div>
                      </div>
                    ) : statsError ? (
                      <div className="text-white">
                        <i className="fas fa-exclamation-circle me-2" />
                        Σφάλμα
                      </div>
                    ) : (
                      <>
                        <h3>{stats?.[`status${status.Code}Inspections`] || 0}</h3>
                        <div>{status.Name}</div>
                        <i
                          className={`fas ${
                            status.Code === 0
                              ? "fa-file-circle-plus"
                              : status.Code === 1
                              ? "fa-clock"
                              : status.Code === 2
                              ? "fa-video"
                              : status.Code === 10
                              ? "fa-clipboard-check"
                              : status.Code === 99
                              ? "fa-ban"
                              : "fa-file"
                          } stats-icon`}
                        />
                      </>
                    )}
                  </div>
                </Link>
              ))}
          </div>
        </div>

        {/* Expert Statistics */}
        {isAdmin() && (
          <div className="mt-4">
            <ExpertStats data={expertStats} loading={expertStatsLoading} error={expertStatsError} />
          </div>
        )}

        {/* Search Box */}
        <Card className="quick-search-card mt-4">
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Row className="g-3">
                <Col xs={12}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder={`ID ελέγχου, Αρ.Κυκλοφορίας, Ιδιοκτήτης${isAdmin() ? ", Expert" : ""}`}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      disabled={searching}
                    />
                    <Button variant="secondary" type="submit" disabled={searching || !searchQuery.trim()}>
                      {searching ? (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <i className="fas fa-search me-2" />
                      )}
                      Αναζήτηση
                    </Button>
                  </InputGroup>
                </Col>
              </Row>
            </Form>

            {/* Results Section */}
            {searching ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Φόρτωση...</span>
                </div>
              </div>
            ) : searchError ? (
              <div className="text-center py-5 text-danger">
                <i className="fas fa-exclamation-circle fa-2x mb-3 d-block" />
                <p className="mb-0">{searchError}</p>
              </div>
            ) : searchResults.length > 0 ? (
              <>
                <hr className="my-4" />
                <DataTable
                  data={searchResults}
                  loading={searching}
                  error={searchError}
                  onRowClick={item => handleInspectionClick(item.InspectionID)}
                  columns={[
                    {
                      header: "Αίτηση",
                      field: "InspectionID",
                      type: "string"
                    },
                    {
                      header: "Όνομα",
                      field: "Name",
                      type: "string"
                    },
                    {
                      header: "Επώνυμο",
                      field: "Surname",
                      type: "string"
                    },
                    {
                      header: "Αρ. Κυκλοφορίας",
                      field: "PlateNumber",
                      type: "string"
                    },
                    {
                      header: "Πραγματογνώμονας",
                      field: "ExpertName",
                      type: "string"
                    },
                    {
                      header: "Status",
                      field: "Status",
                      type: "number",
                      sortValue: item => item.Status,
                      render: item => (
                        <span
                          className={`badge bg-${
                            item.Status === 0
                              ? "secondary"
                              : item.Status === 1
                              ? "warning"
                              : item.Status === 10
                              ? "success"
                              : item.Status === 99
                              ? "danger"
                              : "secondary"
                          }`}>
                          {getAppLookupValue("Status", item.Status)}
                        </span>
                      )
                    }
                  ]}
                />
              </>
            ) : hasSearched ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-search fa-2x mb-3 d-block" />
                <p className="mb-0">Δεν βρέθηκαν αποτελέσματα.</p>
              </div>
            ) : null}
          </Card.Body>
        </Card>
      </div>
    </Layout>
  )
}

export default Dashboard
